import { API_BASE } from '../config';
import { wouldExceedStorageQuota } from './storage-quota';

interface UploadImageResourceParams {
  file: File;
  projectId: string;
  token: string;
  embed?: boolean;
  fromNote?: boolean;
  fromImport?: boolean;
  documentId?: string;
}

interface ApiErrorResponse {
  error?: string;
  errorCode?: string;
}

const throwApiError = (message: string, errorCode?: string): never => {
  const err = new Error(message) as Error & { errorCode?: string };
  if (errorCode) err.errorCode = errorCode;
  throw err;
};

export const uploadImageResourceToS3 = async ({
  file,
  projectId,
  token,
  embed,
  fromNote,
  fromImport,
  documentId,
}: UploadImageResourceParams): Promise<{ id: string }> => {
  const exceedsQuota = await wouldExceedStorageQuota(token, file.size);
  if (exceedsQuota) {
    window.dispatchEvent(new CustomEvent('storage-limit-reached'));
    throwApiError('Storage quota exceeded', 'STORAGE_LIMIT_REACHED');
  }

  const mimeType = file.type || 'application/octet-stream';

  const presignedUrlResponse = await fetch(
    `${API_BASE}/api/projects/${projectId}/resources/presigned-url`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        fileName: file.name,
        fileSize: file.size,
        mimeType,
      }),
    }
  );

  if (!presignedUrlResponse.ok) {
    const errorData = (await presignedUrlResponse.json().catch(() => ({}))) as ApiErrorResponse;
    const message = errorData.error || `Failed to get presigned URL: ${presignedUrlResponse.status}`;
    throwApiError(message, errorData.errorCode);
  }

  const { presignedUrl, s3Key } = await presignedUrlResponse.json();

  const uploadResponse = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': mimeType,
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    const errorData = (await uploadResponse.json().catch(() => ({}))) as ApiErrorResponse;
    const message = errorData.error || `Failed to upload file to S3: ${uploadResponse.status}`;
    throwApiError(message, errorData.errorCode);
  }

  const saveResourceResponse = await fetch(
    `${API_BASE}/api/projects/${projectId}/resources`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        fileName: file.name,
        fileSize: file.size,
        mimeType,
        s3Key,
        embed: embed === true,
        fromNote: fromNote === true,
        fromImport: fromImport === true,
        documentId,
      }),
    }
  );

  if (!saveResourceResponse.ok) {
    const errorData = (await saveResourceResponse.json().catch(() => ({}))) as ApiErrorResponse;
    const message = errorData.error || `Failed to save resource: ${saveResourceResponse.status}`;
    throwApiError(message, errorData.errorCode);
  }

  const saved = (await saveResourceResponse.json()) as { id?: string };
  const resourceId = saved.id;
  if (!resourceId) {
    return throwApiError('Failed to save resource: missing id');
  }

  window.dispatchEvent(new CustomEvent('resource-uploaded'));
  return { id: resourceId };
};

export async function linkResourceToEntity(params: {
  projectId: string;
  token: string;
  resourceId: string;
  entityType: 'character' | 'location';
  entityId: string;
  documentId: string;
}): Promise<void> {
  const response = await fetch(`${API_BASE}/api/projects/${params.projectId}/entity-references`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.token}`,
    },
    body: JSON.stringify({
      sourceType: 'resource',
      sourceId: params.resourceId,
      sourceDocumentId: null,
      targetType: params.entityType,
      targetId: params.entityId,
      targetDocumentId: params.documentId,
    }),
  });
  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as ApiErrorResponse;
    throwApiError(errorData.error || `Failed to link resource: ${response.status}`, errorData.errorCode);
  }
}

export async function linkResourceToCharacter(params: {
  projectId: string;
  token: string;
  resourceId: string;
  characterId: string;
  documentId: string;
}): Promise<void> {
  await linkResourceToEntity({
    projectId: params.projectId,
    token: params.token,
    resourceId: params.resourceId,
    entityType: 'character',
    entityId: params.characterId,
    documentId: params.documentId,
  });
}

